<template>
  <list-layout
    :title="(!isSubTab ? $t('transactions') : '') | startcase"
    :search-filter="!payoutsTransactions ? filters.search: null"
    :apply-filters="onFiltersApply"
    no-counts
    no-apply-btn
    no-search
  >
    <modal
      id="transactions-export-modal"
      :customTitle="$t('export_transactions_list') | capitalize"
      modal-class="custom-modal edit"
      modalType="edit"
      size="l"
      centered
      hide-footer>
        <transactions-export-form :count="count"/>
    </modal>
    <template v-slot:beforeFilters>
      <b-row v-if="!isSubTab">
        <b-col>
          <tabs-layout ref="transactionsTabs" :tabs="tabs" @change="onTabChange">
            <template v-slot:tabTitle="{ tab }">
              {{ tab.title }}
            </template>
          </tabs-layout>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <filter-builder
            v-if="currentTab === 'transactions_tab' && !isRemovalRequest"
            :id="filterBuilderId"
            ref="transactionFilters"
            :filters="activeFilters"
            :apply-filters="onFiltersApply"
            :reset-filters="resetFilters"
            :filters-pending="filtersPending"
            :filters-applied="filtersApplied"
            @filter="onFilter"
          />
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center">
        <b-col>
          <b-form-radio-group
            v-if="isSubTab && !payoutsTransactions && currentTab !== 'payouts_tab'"
            v-model="currentTab"
            :options="transactionsOptions"
            buttons
            button-variant="outline-secondary"
            name="radios-btn-outline"
            class="radio-buttons mr-3"
          />
          <b-form-radio-group
            v-if="firstLoaded && currentTab === 'transactions_tab' && !isRemovalRequest"
            v-model="transactionsTable.selectedAggregation"
            :options="transactionsTable.aggregationOptions"
            buttons
            button-variant="outline-secondary"
            name="radios-btn-outline"
            class="radio-buttons"
          />
        </b-col>
      </b-row>
    </template>
    <template v-slot:table>
      <div
        v-if="!firstLoaded && currentTab === 'transactions_tab'"
        class="w-100 d-flex flex-column justify-content-center align-items-center flex-fill"
      >
        <div class="mt-3 mb-3">
          <b-img :src="require('@/assets/no_filter.png')" class="no-filter-img"/>
        </div>
        <div class="no-filters-text">
          {{ $t('transactions_select_filter_text') }}
        </div>
        <div>
          <b-button class="btn-secondary" @click="loadAllTransactions" id="load-transactions-btn">
            {{ $t('load_all_transactions') }}
          </b-button>
        </div>
      </div>
      <t-table
        v-if="firstLoaded && currentTab === 'transactions_tab' && !isRemovalRequest"
        :columns="transactionColumns"
        :options="transactionTableOptions"
        :limit="limit"
        :id="transactionTableId"
        :key="transactionTableId"
        :keepColumnOrder="true"
        ref="transactions"
        @loaded="onLoaded"
        @loading="onLoading"
        @pagination="onPagination"
        @sorted="onSorted"
      >
        <template v-slot:type="{ row }">
          {{ $t(row.type) | capitalize }}
        </template>
        <!-- Performer Category -->
        <template v-slot:performerCategory="{ row }">
          {{ row.performerCategory ? $t(transactionPerformerCategories[row.performerCategory]) : "" | capitalize }}
        </template>
        <template v-slot:merge>
          <span v-if="isSubTab && !payoutsTransactions">
            <b-button size="sm"
              class="ml-2 btn-outline-red"
              :to="{
                name: 'transactions-list',
                query: {
                  recordingProprietaryId: recordingResoundId,
                  rightsHolderProprietaryId: rightsholderResoundId,
                  distribution_id: distributionId,
                  distribution: distributionValue,
                  },
              }">
              {{ $t("view_transactions") | capitalize }}
            </b-button>
          </span>
          <span class="d-flex align-items-center">
            <slot name="mergeButtons" />
            <b-button
              id="export-btn"
              size="sm"
              @click="exportData"
              class="ml-2 btn-outline-red"
            >
              <fa-icon size="sm" :icon="['fa', 'cloud-upload-alt']" />
              {{ $t("export") | capitalize }}
            </b-button>
            <b-form-checkbox
              v-if="transactionsTable.selectedAggregation === 'none'"
              class="d-flex align-items-center mt-1 ml-3"
              v-model="filters.showAdjustment.value"
              @change="applyShowAdjustmentFilter"
              switch
            >
              {{ $t("adjustment_transactions") | capitalize }}
            </b-form-checkbox>
            <b-form-checkbox
              v-if="transactionsTable.selectedAggregation === 'none'"
              class="d-flex align-items-center mt-1 ml-3"
              v-model="filters.showBlocked.value"
              @change="applyShowBlockedFilter"
              switch
            >
              {{ $t("blocked_transactions") | capitalize }}
            </b-form-checkbox>
            <b-form-checkbox
                class="d-flex align-items-center mt-1 ml-3"
                v-model="filters.applyPrecision.value"
                @change="applyPrecisionFilter"
                switch
            >
              {{ $t("apply_rounding") | capitalize }}
            </b-form-checkbox>
            <b-form-checkbox
              class="ml-3"
              v-model="isRemovalRequest"
              switch
              v-if="filters.transactionTransactionBatchId.value != null"
            >
            <!-- TODO set to false when batch_id filter is removed -->
              {{ $t("view_removal_requests") | capitalize }}
            </b-form-checkbox>
          </span>
        </template>
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
              class="smaller p-0"
              :page="page"
              :count="count"
              :loading="loading"
              v-model="limit"
              @limit="onLimit"
            />
          </div>
        </template>

        <!-- Yes / No fields -->

        <template v-slot:payable="{ row }">
          <template v-if="row.payable">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:preview="{ row }">
          <template v-if="row.preview">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:hold="{ row }">
          <template v-if="row.hold">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:mandateConflict="{ row }">
          <template v-if="row.mandateConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:ownershipConflict="{ row }">
          <template v-if="row.ownershipConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:lineupConflict="{ row }">
          <template v-if="row.lineupConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:genreConflict="{ row }">
          <template v-if="row.genreConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:eligibility="{ row }">
          <template v-if="row.eligibility === 'eligible'">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:pendingMakerMatching="{ row }">
          <template v-if="row.pendingMakerMatching">
           <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:pendingPerformerMatching="{ row }">
          <template v-if="row.pendingPerformerMatching">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:missingMandate="{ row }">
          <template v-if="!row.missingMandate">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:hasFeaturedCount="{ row }">
          <template v-if="row.hasFeaturedCount">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:hasNonFeaturedCount="{ row }">
          <template v-if="row.hasNonFeaturedCount">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:paymentStatus="{ row }">
          {{ getPaymentStatusDisplay(row.paymentStatus) }}
        </template>

        <!--  Link value fields-->

        <template v-slot:distributionId="{ row }">
          <database-link
            :type="linkTypes.DISTRIBUTION"
            :item-id="row.distributionId"
          >
            {{ row.distributionTariffCode }}
            {{ row.distributionPeriodStart | truncate(7) }} - {{ row.distributionPeriodEnd | truncate(7) }}
          </database-link>
        </template>
        <template v-slot:distributionTariffCode="{ row }">
            {{ tariffDisplay(row.distributionTariffCode) }}
        </template>
        <template v-slot:distributionPeriodStart="{ row }">
          {{ parseDate(row.distributionPeriodStart, $config.DATE_FORMAT) }}
        </template>
        <template v-slot:distributionPeriodEnd="{ row }">
          {{ parseDate(row.distributionPeriodEnd, $config.DATE_FORMAT) }}
        </template>
        <template v-slot:adjustmentTypes="{ row }">
          {{ row.adjustmentTypes.filter(at => !Object.values(blockedAdjustmentTypes).includes(at)).join(", ") }}
        </template>
        <template v-slot:blockedTypes="{ row }">
          {{ row.adjustmentTypes.filter(at => Object.values(blockedAdjustmentTypes).includes(at)).join(", ") }}
        </template>
        <template v-slot:rightsHolderProprietaryId="{ row }">
          <database-link
            :type="linkTypes.RIGHTS_HOLDER"
            :item-id="row.rightsHolder?.id"
          >
            {{ row.rightsHolderProprietaryId }}
          </database-link>
        </template>
        <template v-slot:name="{ row }">
          <database-link
            :type="linkTypes.RIGHTS_HOLDER"
            :item-id="row.rightsHolder?.id"
          >
            {{ row.name }}
          </database-link>
        </template>
        <template v-slot:recordingProprietaryId="{ row }">
          <database-link
            :type="linkTypes.RECORDING"
            :item-id="row.soundRecording?.id"
          >
            {{ row.recordingProprietaryId }}
          </database-link>
        </template>
        <template v-slot:title="{ row }" >
          <database-link :type="linkTypes.RECORDING" :item-id="row.soundRecording?.id">
            {{ row.title }}
          </database-link>
        </template>

        <template v-slot:payoutId="{ row }" >
          <database-link :type="linkTypes.PAYOUT" :item-id="row.payoutId">
            {{ row.payoutDisplayId }}
          </database-link>
        </template>

        <!-- Multiple value fields -->
        <template v-slot:localProprietaryIds="{ row }">
          {{ getProprietaryIds(row.identifiers).length ? getProprietaryIds(row.identifiers)[0].code : "" }}
          <fa-icon
            v-if="getProprietaryIds(row.identifiers).length"
            class="blue clickable ml-2"
            :icon="['fas', 'plus-circle']"
            @click="showModal(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`)"
          />
          <b-modal
            :id="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`"
            :ref="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`"
            modal-class="custom-modal info"
            size="xl"
            centered
            hide-footer
          >
            <template #modal-title>
              <h4 class="d-flex align-items-center mb-0">
                <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2"/>
                {{ $t('proprietary_ids') }}
              </h4>
            </template>
            <t-table
                :columns="localProprietaryIdsColumns"
                :options="localProprietaryIdsOptions"
                id="local-proprietary-ids-table"
                :local-data="getProprietaryIds(row.identifiers)"
            >
              <template v-slot:id="{ row: proprietaryIds }">
                {{ proprietaryIds.code }}
              </template>

              <template v-slot:mc="{ row: proprietaryIds }">
                {{ proprietaryIds.type }}
              </template>
            </t-table>
            <b-row class="modal-footer float-right mt-4">
              <b-button class="btn-outline-red" @click="$bvModal.hide(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`)">
                {{ $t("ok") | capitalize }}
              </b-button>
            </b-row>
          </b-modal>
        </template>

        <!-- Multiple value fields -->
        <template v-slot:localRightsholderProprietaryIds="{ row }">
          {{ getProprietaryIds(row.rightsholderIdentifiers).length ? getProprietaryIds(row.rightsholderIdentifiers)[0].code : "" }}
          <fa-icon
            v-if="getProprietaryIds(row.rightsholderIdentifiers).length"
            class="blue clickable ml-2"
            :icon="['fas', 'plus-circle']"
            @click="showModal(`rightsholder-${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`)"
          />
          <b-modal
            :id="`rightsholder-${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`"
            :ref="`rightsholder-${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`"
            modal-class="custom-modal info"
            size="xl"
            centered
            hide-footer
          >
            <template #modal-title>
              <h4 class="d-flex align-items-center mb-0">
                <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2"/>
                {{ $t('rights_holder_proprietary_ids') }}
              </h4>
            </template>
            <t-table
                :columns="localProprietaryIdsColumns"
                :options="localProprietaryIdsOptions"
                id="local-rightsholder-proprietary-ids-table"
                :local-data="getProprietaryIds(row.rightsholderIdentifiers)"
            >
              <template v-slot:id="{ row: proprietaryIds }">
                {{ proprietaryIds.code }}
              </template>

              <template v-slot:mc="{ row: proprietaryIds }">
                {{ proprietaryIds.type }}
              </template>
            </t-table>
            <b-row class="modal-footer float-right mt-4">
              <b-button class="btn-outline-red" @click="$bvModal.hide(`rightsholder-${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-proprietary-ids`)">
                {{ $t("ok") | capitalize }}
              </b-button>
            </b-row>
          </b-modal>
        </template>

        <!-- Number fields -->

        <template v-slot:totalPlaycount="{ row }">
          {{ row.deltaPlaycount | formatNumber(2) }}
        </template>

        <template v-slot:transactionsCount="{ row }">
          {{ row.transactionsCount | formatNumber }}
        </template>

        <!-- Money amount fields -->

        <template v-slot:totalAmount="{ row }">
          ${{ row.deltaTotalAmount | formatNumber(2) }}
        </template>
        <template v-slot:paidNetAmount="{ row }">
          ${{ row.paidNetAmount }}
        </template>
        <template v-slot:recordingBalance="{ row }">
          ${{ row.recordingBalance | formatNumber(2, 2) }}
        </template>
        <template v-slot:payableRawAmount="{ row }">
          ${{ row.payableRawAmount | formatNumber(2, 2) }}
        </template>
        <template v-slot:payableAmount="{ row }">
          ${{ row.deltaPayableAmount | formatNumber(2) }}
        </template>
        <template v-slot:vpp="{ row }">
          {{ row.vpp }}
        </template>
        <template v-slot:baseFee="{ row }">
          ${{ row.baseFee }}
        </template>
        <template v-slot:internationalIncomingFee="{ row }">
          ${{ row.internationalIncomingFee }}
        </template>
        <template v-slot:serviceFee="{ row }">
          ${{ row.serviceFee }}
        </template>
        <template v-slot:withholdingTax="{ row }">
          ${{ row.withholdingTax }}
        </template>

        <!--  Percent fields  -->

        <template v-slot:share="{ row }">
          {{ row.share | formatNumber(2) }}%
        </template>

        <!--  Datetime fields -->

        <template v-slot:calculationTimestamp="{ row }">
          {{ parseDate(row.calculationTimestamp, $config.DATETIME_FORMAT) }}
        </template>

        <template v-slot:paidDate="{ row }">
          {{ parseDate(row.paidDate, $config.DATETIME_FORMAT) }}
        </template>

        <!-- Action fields -->

        <template v-slot:removeTransaction="{ row }">
          <div
            class="d-flex"
            v-if="(permissions.actions.canRemoveMcTransactions && userMemberCollectiveMatchesRow(row)) || permissions.actions.canRemoveTransactions"
          >
          <b-button
            class="ml-3"
            :class="removeRowDisabled(row) ? 'btn-outline-dark' : 'btn-outline-red'"
            :disabled="removeRowDisabled(row)"
            @click="handleRemoveTransaction(row)"
          >
            {{ $t("remove") | capitalize }}
          </b-button>
          </div>
        </template>

        <template v-slot:refreshTransaction="{ row }">
          <div
            class="d-flex"
            v-if="(permissions.actions.canRefreshMcTransactions && userMemberCollectiveMatchesRow(row)) || permissions.actions.canRefreshTransactions"
          >
            <b-button class="ml-3" variant="outline-primary" @click="handleRefreshTransaction(row)">
              {{ $t("refresh") | capitalize }}
            </b-button>
          </div>
        </template>

        <template v-slot:moratorium="{ row }">
          <template v-if="row.moratorium">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:validPayee="{ row }">
          <template v-if="row.validPayee">
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
        </template>

        <template v-slot:recordingBalanceExceeded="{ row }">
          <template v-if="row.recordingBalanceExceeded">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:drillDown="{ row }">
          <fa-icon
            v-if="row.aggregatedData && row.aggregatedData.length > 1"
            class="blue clickable ml-2"
            :icon="['fas', 'plus-circle']"
            @click="showModal(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-${row.payoutId}`)"
          />
          <b-modal
            :id="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-${row.payoutId}`"
            :ref="`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-${row.payoutId}`"
            modal-class="custom-modal info"
            size="xl"
            centered
            hide-footer
          >
            <template #modal-title>
              <h4 class="d-flex align-items-center mb-0">
                <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2"/>
                {{ $t('consolidated_share_details') | startcase }}
              </h4>
            </template>
            <t-table
                :columns="drillDownTableColumns"
                :options="drillDownTableOptions"
                id="drill-down-transactions-table"
                :local-data="row.aggregatedData"
            >
              <template v-slot:totalAmount="{ row: aggregatedRow }">
                ${{ aggregatedRow.totalAmount }}
              </template>

              <template v-slot:payableAmount="{ row: aggregatedRow }">
                ${{ aggregatedRow.payableAmount }}
              </template>

              <template v-slot:rawAmount="{ row: aggregatedRow }">
                ${{ aggregatedRow.rawAmount }}
              </template>

              <template v-slot:totalPlaycount="{ row: aggregatedRow }">
                {{ aggregatedRow.totalPlaycount }}
              </template>

              <template v-slot:share="{ row: aggregatedRow }">
                {{ aggregatedRow.share }}%
              </template>

              <template v-slot:name>
                <database-link
                    :type="linkTypes.RIGHTS_HOLDER"
                    :item-id="row.rightsHolder?.id"
                >
                  {{ row.name }}
                </database-link>
              </template>

              <template v-slot:payable>
                <template v-if="row.payable">
                  <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
                </template>
                <template v-else>
                  <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
                </template>
              </template>

              <template v-slot:baseFee="{ row: aggregatedRow }">
                ${{ aggregatedRow.baseFee }}
              </template>

              <template v-slot:internationalIncomingFee="{ row: aggregatedRow }">
                ${{ aggregatedRow.internationalIncomingFee }}
              </template>

              <template v-slot:serviceFee="{ row: aggregatedRow }">
                ${{ aggregatedRow.serviceFee }}
              </template>

              <template v-slot:withholdingTax="{ row: aggregatedRow }">
                ${{ aggregatedRow.withholdingTax }}
              </template>

              <template v-slot:broadcastStartDate="{ row: aggregatedRow }">
                {{ aggregatedRow.broadcastStartDate }}
              </template>

              <template v-slot:broadcastEndDate="{ row: aggregatedRow }">
                {{ aggregatedRow.broadcastEndDate }}
              </template>

            </t-table>
            <b-row class="modal-footer float-right mt-4">
              <b-button class="btn-outline-red" @click="$bvModal.hide(`${row.recordingProprietaryId}-${row.payeeId}-${row.rightsHolderProprietaryId}-${row.distributionId}-${row.payoutId}`)">
                {{ $t("ok") | capitalize }}
              </b-button>
            </b-row>
          </b-modal>
        </template>
      </t-table>
      <div v-if="currentTab === 'payouts_tab'">
        <ListPayoutsPage
          :isSubTab="true"
          :isTransactionsSubTab="isSubTab"
          :current-tab.sync="currentTab"
          :transactions-options="transactionsOptions"
          :distributionId="distributionId"
          :recordingResoundId="recordingResoundId"
          :rightsholderResoundId="rightsholderResoundId"
          @applyPayoutFilter="applyPayoutFilter"
        />
      </div>
      <div v-if="currentTab === 'transactions_tab' && isRemovalRequest">
        <RemovalRequests
          :payout-id="actualPayoutData.id"
          @closeRemovalRequest="handleCloseRemovalRequest"
        />
      </div>
      <modal
          id="refresh-modal"
          :customTitle="$t('refresh_request') | capitalize"
          modal-class="custom-modal info"
          modalType="edit"
          size="lg"
          @ok="transactionRefreshRequest"
          centered
          okTitle="Confirm"
          @close="cancelRefresh"
        >
          {{ refreshMessage() }}
      </modal>
      <modal
        id="removal-modal"
        modal-type="edit"
        centered
        size="lg"
        hide-footer
        noCloseOnBackdrop
        noCloseOnEsc
        @close="cancelRemove"
        :custom-title="$t('remove_transaction') | capitalize"
      >
        {{ removalMessage() }}
        <b-form @submit.prevent="transactionRemovalRequest">
          <b-form-group
            class="mt-4"
            :label="$t('reason') | capitalize"
            label-class="pb-0"
            label-cols="2"
          >
            <b-form-select
              v-model="removeTransaction.reason"
              :options="removalOptions"
            />
          </b-form-group>
          <b-form-group
            label-cols="2"
          >
            <b-form-textarea
              size="lg"
              class="mb-4 mt-4"
              debounce="500"
              name="comment"
              :placeholder="$t('make_comment') | capitalize"
              v-model="removeTransaction.comment"
              rows="5"
            />
          </b-form-group>
          <b-row class="modal-footer padding">
            <b-button @click="cancelRemove" class="btn-outline-dark">
              {{ $t("cancel") | capitalize }}
            </b-button>
            <b-button type="submit" class="btn-outline-red"
              :disabled="!(removeTransaction.comment && removeTransaction.reason)"
            >
              {{ $t("confirm") | capitalize }}
            </b-button>
          </b-row>
        </b-form>
      </modal>

      <modal
        id="aggregation-filters-warning-modal"
        :customTitle="$t('filters_not_applicable_title') | capitalize"
        modal-class="custom-modal info"
        modalType="info"
        size="lg"
        centered
        :ok-title="$t('confirm') | capitalize"
        :cancel-title="$t('cancel') | capitalize"
        @ok="confirmAggregationChange"
        @cancel="cancelAggregationChange"
        @close="cancelAggregationChange"
      >
        <p>{{ $t('filters_not_applicable_in_aggregation') }}</p>
        <ul>
          <li v-for="label in inapplicableFilterLabels" :key="label">{{ label }}</li>
        </ul>
      </modal>
    </template>
    <slot v-if="payoutsTransactions" name="footerButtons" />
  </list-layout>
</template>

<script>
import {
  blockedAdjustmentTypes,
  distributionCollegeTypesTransactionsList,
  filterTypes,
  linkTypes,
  payoutJobExecutionStatuses,
  pendingQueueAggregations,
  perPageItemsMd,
  transactionAggregations,
  transactionPaymentStatus,
  transactionPerformerCategories,
  transactionRemovalStatuses,
  userPreferenceNames
} from "@/constants"
import { capitalize, snakeCase, startCase } from "lodash"
import { mapActions, mapGetters, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder.vue"
import ListPayoutsPage from "@/pages/Payouts/ListPayoutsPage"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination.vue"
import RemovalRequests from "./RemovalRequests.vue"
import TransactionsExportForm from "./TransactionsExportForm.vue"
import { listRouteMixin } from "@/utils/mixins"

import parseDate from "@/utils/date-parser"

const aggregationFilters = {
  "none": [
    "search",
    "rightsHolderProprietaryId",
    "ipn",
    "rightsholderIdentifierCode",
    "payeeId",
    "paymentStatus",
    "payeeName",
    "societyName",
    "type",
    "performerCategory",
    "distribution",
    "distributionPeriodStart",
    "distributionPeriodEnd",
    "distributionTariffCode",
    "tariffPeriodYear",
    "recordingProprietaryId",
    "artist",
    "isrc",
    "identifiers",
    "totalPlaycount",
    "paidNetAmount",
    "paidDate",
    "recordingBalance",
    "payableRawAmount",
    "vpp",
    "totalAmount",
    "baseFee",
    "internationalIncomingFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "calculationTimestamp",
    "eligibility",
    "lineupConflict",
    "mandateConflict",
    "genreConflict",
    "hold",
    "missingMandate",
    "hasFeaturedCount",
    "hasNonFeaturedCount",
    "preview",
    "payable",
    "pendingMakerMatching",
    "pendingPerformerMatching",
    "ownershipConflict",
    "moratorium",
    "validPayee",
    "recordingBalanceExceeded",
    "share",
    "abovePayoutThreshold",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
    "closeoutId",
    "distributionLicenseeName",
    "distributionInvoiceNumber",
    "identifiersCodes",
  ],
  "rightsHolder": [
    "search",
    "rightsHolderProprietaryId",
    "ipn",
    "rightsholderIdentifierCode",
    "payeeName",
    "totalAmount",
    "baseFee",
    "internationalIncomingFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "paidNetAmount",
    "societyName",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "payeeTariff": [
    "search",
    "payeeId",
    "paymentStatus",
    "payeeName",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "totalAmount",
    "baseFee",
    "internationalIncomingFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "paidNetAmount",
    "distributionLicenseeName",
    "distributionInvoiceNumber",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "rightsHolderTariffPeriod": [
    "search",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "rightsHolderProprietaryId",
    "ipn",
    "rightsholderIdentifierCode",
    "payeeId",
    "paymentStatus",
    "payeeName",
    "societyName",
    "totalAmount",
    "baseFee",
    "internationalIncomingFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "paidNetAmount",
    "distributionLicenseeName",
    "distributionInvoiceNumber",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "rightsHolderTariffRecording": [
    "search",
    "distribution",
    "distributionTariffCode",
    "tariffPeriodYear",
    "recordingProprietaryId",
    "artist",
    "rightsHolderProprietaryId",
    "ipn",
    "rightsholderIdentifierCode",
    "societyName",
    "totalAmount",
    "baseFee",
    "internationalIncomingFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "paidNetAmount",
    "share",
    "totalPlaycount",
    "type",
    "distributionLicenseeName",
    "distributionInvoiceNumber",
    "identifiersCodes",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
  "recordingRightsHolder": [
    "search",
    "artist",
    "rightsHolderProprietaryId",
    "recordingProprietaryId",
    "performerCategory",
    "societyName",
    "totalAmount",
    "baseFee",
    "internationalIncomingFee",
    "serviceFee",
    "withholdingTax",
    "payableAmount",
    "paidNetAmount",
    "share",
    "totalPlaycount",
    "type",
    "transactionTransactionBatchId",
    "transactionPayoutCompletedId",
  ],
}

function toSortParam (param) {
  return snakeCase(param)
}

export default {
  name: "TransactionsList",
  mixins: [listRouteMixin],
  components: { TransactionsExportForm, DatabaseLink, Pagination, FilterBuilder, ListPayoutsPage, RemovalRequests },
  props: {
    recordingResoundId: {
      type: String,
      required: false,
    },
    rightsholderResoundId: {
      type: String,
      required: false,
    },
    distributionId: {
      type: String,
      required: false,
    },
    distributionValue: {
      type: String,
      required: false,
    },
    payoutData: {
      type: Object,
      required: false,
    },
    payoutsTransactions: {
      type: Boolean,
      default: false,
    },
    isSubTab: {
      type: Boolean,
      required: false,
    }
  },
  computed: {
    ...mapState("consts", ["memberCollectives", "tariffs"]),
    ...mapState("user", ["permissions"]),
    ...mapGetters("user", ["hasMakers", "hasPerformers"]),
    typeFilterOptions () {
      return Object.values(distributionCollegeTypesTransactionsList)
        .filter(v => {
          if (v === "maker") return this.hasMakers
          if (v === "performer") return this.hasPerformers
          return true
        })
        .map(v => ({ value: v, text: capitalize(this.$t(v)) }))
    },
    actualPayoutData () {
      return this.payoutData || this.filteredPayoutData
    },
    activeFilters () {
      // hide filters not available for selected aggregation
      const aggregationKey = this.transactionsTable.selectedAggregation
      const payoutFilterActive = !this.isSubTab && !!(
        this.filters.transactionPayoutCompletedId.value ||
        this.filters.transactionTransactionBatchId.value
      )
      const lockPayoutCompanions = payoutFilterActive && aggregationKey !== "none"
      const allowedFilters = this.getAllowedFilters(aggregationKey)
      Object.keys(this.filters).forEach(key => {
        this.filters[key].hidden = !allowedFilters.has(key)

        if (key === "recordingProprietaryId" && this.recordingResoundId) {
          this.filters[key].hidden = true
        } else if (key === "rightsHolderProprietaryId" && this.rightsholderResoundId) {
          this.filters[key].hidden = true
        } else if (key === "distribution" && this.distributionId) {
          this.filters[key].hidden = true
        } else if (key === "transactionTransactionBatchId") {
          this.filters.transactionTransactionBatchId.disable = !!this.isSubTab
        } else if (key === "transactionPayoutCompletedId") {
          this.filters.transactionPayoutCompletedId.disable = !!this.isSubTab
          if (this.isSubTab && this.payoutData?.payoutId) {
            this.filters.abovePayoutThreshold.disable = true
            this.filters.payable.disable = true
          }
        } else if ((key === "payable" || key === "abovePayoutThreshold") && !this.isSubTab) {
          this.filters[key].disable = lockPayoutCompanions
        }
      })
      return Object.fromEntries(Object.entries(this.filters))
    },
    filterBuilderId () {
      let id = userPreferenceNames.TRANSACTIONS_LIST_FILTER
      if (this.distributionId) {
        id = id + "_distribution"
      } else if (this.rightsholderResoundId) {
        id = id + "_rights_holder"
      } else if (this.recordingResoundId) {
        id = id + "_recording"
      } else if (this.payoutData?.id) {
        id = id + "_payout"
      }
      return id
    },
    transactionTableId () {
      return `transactions-${this.committedAggregation}`
    },
    transactionColumns () {
      let cols = []
      switch (this.committedAggregation) {
      case "rightsHolder":
        cols = [
          "rightsHolderProprietaryId",
          "name",
          "ipn",
          "localRightsholderProprietaryIds",
          "payeeName",
          "societyName",
          "totalAmount",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "paidNetAmount",
          "transactionsCount",
          "removeTransaction",
          "refreshTransaction",
        ]
        break
      case "payeeTariff":
        cols = [
          "payeeId",
          "payeeName",
          "paymentStatus",
          "distributionId",
          "distributionTariffCode",
          "distributionPeriodStart",
          "distributionPeriodEnd",
          "totalAmount",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "paidNetAmount",
          "transactionsCount",
          "distributionLicenseeName",
          "distributionInvoiceNumber",
        ]
        break
      case "rightsHolderTariffPeriod":
        cols = [
          "distributionId",
          "distributionTariffCode",
          "distributionPeriodStart",
          "distributionPeriodEnd",
          "payeeId",
          "paymentStatus",
          "rightsHolderProprietaryId",
          "name",
          "ipn",
          "localRightsholderProprietaryIds",
          "payeeName",
          "societyName",
          "totalAmount",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "paidNetAmount",
          "transactionsCount",
          "distributionLicenseeName",
          "distributionInvoiceNumber",
        ]
        break
      case "rightsHolderTariffRecording":
        cols = [
          "distributionId",
          "distributionTariffCode",
          "distributionPeriodStart",
          "distributionPeriodEnd",
          "rightsHolderProprietaryId",
          "recordingProprietaryId",
          "title",
          "artist",
          "localProprietaryIds",
          "type",
          "performerCategory",
          "name",
          "ipn",
          "localRightsholderProprietaryIds",
          "societyName",
          "totalPlaycount",
          "share",
          "totalAmount",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "paidNetAmount",
          "calculationTimestamp",
          "transactionsCount",
          "drillDown",
          "removeTransaction",
          "refreshTransaction",
          "distributionLicenseeName",
          "distributionInvoiceNumber",
        ]
        break
      case "recordingRightsHolder":
        cols = [
          "rightsHolderProprietaryId",
          "recordingProprietaryId",
          "title",
          "artist",
          "type",
          "performerCategory",
          "name",
          "societyName",
          "totalPlaycount",
          "share",
          "totalAmount",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "paidNetAmount",
          "transactionsCount",
          "drillDown",
          "removeTransaction",
          "refreshTransaction",
        ]
        break
      default:
        cols = [
          "payable",
          "preview",
          "rightsHolderProprietaryId",
          "name",
          "ipn",
          "localRightsholderProprietaryIds",
          "payeeId",
          "paymentStatus",
          "payeeName",
          "societyName",
          "type",
          "performerCategory",
          "recordingProprietaryId",
          "distributionId",
          "distributionTariffCode",
          "distributionPeriodStart",
          "distributionPeriodEnd",
          "adjustmentTypes",
          "blockedTypes",
          "title",
          "artist",
          "isrc",
          "localProprietaryIds",
          "totalPlaycount",
          "vpp",
          "totalAmount",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "payableAmount",
          "paidNetAmount",
          "paidDate",
          "recordingBalance",
          "payableRawAmount",
          "share",
          "calculationTimestamp",
          "eligibility",
          "pendingMakerMatching",
          "pendingPerformerMatching",
          "lineupConflict",
          "mandateConflict",
          "genreConflict",
          "ownershipConflict",
          "missingMandate",
          "hasFeaturedCount",
          "hasNonFeaturedCount",
          "payoutId",
          "hold",
          "moratorium",
          "validPayee",
          "recordingBalanceExceeded",
          "transactionId",
          "closeoutId",
          "drillDown",
          "distributionLicenseeName",
          "distributionInvoiceNumber",
        ]
      }
      return cols.filter((col) => this.filters.transactionTransactionBatchId.value || !["removeTransaction", "refreshTransaction"].includes(col))
    },
    transactionTableOptions () {
      let options = {
        allSelected: false,
        sortable: [
          "distributionId",
          "distributionTariffCode",
          "distributionPeriodStart",
          "distributionPeriodEnd",
          "payeeId",
          "paymentStatus",
          "rightsHolderProprietaryId",
          "recordingProprietaryId",
          "name",
          "ipn",
          "payeeName",
          "societyName",
          "type",
          "performerCategory",
          "title",
          "artist",
          "isrc",
          "transactionId",
          "hold",
          "preview",
          "payable",
          "mandateConflict",
          "hasFeaturedCount",
          "hasNonFeaturedCount",
          "ownershipConflict",
          "eligibility",
          "lineupConflict",
          "moratorium",
          "validPayee",
          "recordingBalanceExceeded",
          "genreConflict",
          "pendingPerformerMatching",
          "pendingMakerMatching",
          "totalAmount",
          "payableAmount",
          "paidNetAmount",
          "paidDate",
          "recordingBalance",
          "payableRawAmount",
          "totalPlaycount",
          "share",
          "payeeShare",
          "vpp",
          "deltaTotalAmount",
          "deltaPayableAmount",
          "deltaPlaycount",
          "calculationTimestamp",
          "deltaShare",
          "transactionsCount",
          "missingMandate",
          "payoutId",
          "baseFee",
          "internationalIncomingFee",
          "serviceFee",
          "withholdingTax",
          "distributionLicenseeName",
          "distributionInvoiceNumber",
          "closeoutId",
        ],
        headings: {
          "rightsHolderProprietaryId": this.$t("rights_holder_id"),
          "name": this.$t("rights_holder_name"),
          "ipn": this.$t("ipn"),
          "localRightsholderProprietaryIds": this.$t("rights_holder_proprietary_ids"),
          "payeeId": this.$t("payee_id"),
          "paymentStatus": this.$t("payment_status"),
          "payeeName": this.$t("payee_name"),
          "societyName": this.$t("collective_society"),
          "type": capitalize(this.$t("type")),
          "performerCategory": capitalize(this.$t("category")),
          "recordingProprietaryId": this.$t("recording_id"),
          "transactionId": this.$t("transaction_id"),
          "closeoutId": this.$t("closeout_id"),
          "title": capitalize(this.$t("recording_title")),
          "distributionId": this.$t("tariff_period_title"),
          "distributionTariffCode": capitalize(this.$t("tariff")),
          "distributionPeriodStart": capitalize(this.$t("tariff_period_start")),
          "distributionPeriodEnd": capitalize(this.$t("tariff_period_end")),
          "adjustmentTypes": startCase(this.$t("adjustment_reasons")),
          "blockedTypes": startCase(this.$t("blocked_transactions")),
          "artist": capitalize(this.$t("artist")),
          "isrc": this.$t("isrc"),
          "localProprietaryIds": this.$t("proprietary_ids"),
          "hold": this.$t("no_hold"),
          "preview": capitalize(this.$t("preview")),
          "payable": capitalize(this.$t("payable")),
          "mandateConflict": capitalize(this.$t("no_mandate_conflict")),
          "ownershipConflict": this.$t("no_ownership_conflict"),
          "eligibility": capitalize(this.$t("eligible")),
          "lineupConflict": this.$t("no_lineup_conflict_title"),
          "moratorium": capitalize(this.$t("no_moratorium")),
          "validPayee": capitalize(this.$t("valid_payee")),
          "recordingBalanceExceeded": capitalize(this.$t("no_recording_balance_exceeded")),
          "genreConflict": this.$t("no_genre_conflict"),
          "pendingPerformerMatching": this.$t("matched_performer"),
          "pendingMakerMatching": this.$t("matched_maker"),
          "totalAmount": capitalize(this.$t("amount_gross")),
          "payableAmount": capitalize(this.$t("net_amount")),
          "paidNetAmount": capitalize(this.$t("paid_net_amount")),
          "paidDate": capitalize(this.$t("paid_date")),
          "recordingBalance": capitalize(this.$t("recording_balance")),
          "payableRawAmount": capitalize(this.$t("payable_raw_amount")),
          "share": capitalize(this.$t("share")),
          "payeeShare": this.$t("share_payee"),
          "vpp": this.$t("vpp"),
          "totalPlaycount": this.$t("total_playcount"),
          "deltaTotalAmount": this.$t("amount_total_delta"),
          "deltaPayableAmount": this.$t("amount_net_delta"),
          "deltaPlaycount": this.$t("total_playcount_delta"),
          "calculationTimestamp": this.$t("transaction_calculation_timestamp"),
          "deltaShare": this.$t("share_delta"),
          "transactionsCount": this.$t("transactions_count"),
          "missingMandate": capitalize(this.$t("mandate")),
          "hasFeaturedCount": capitalize(this.$t("featured_count")),
          "hasNonFeaturedCount": this.$t("non_featured_count"),
          "payoutId": capitalize(this.$t("payout")),
          "baseFee": capitalize(this.$t("base_fees")),
          "internationalIncomingFee": capitalize(this.$t("international_incoming_fees")),
          "serviceFee": capitalize(this.$t("service_fees")),
          "withholdingTax": capitalize(this.$t("withholding_tax")),
          "drillDown": capitalize(this.$t("drill_down")),
          "removeTransaction": "",
          "refreshTransaction": "",
          "distributionLicenseeName": capitalize(this.$t("licensee_name")),
          "distributionInvoiceNumber": capitalize(this.$t("invoice_number")),
        },
        width: {
          "removeTransaction": "100",
          "refreshTransaction": "100",
          "paymentStatus": "100",
        },
        popups:{
          adjustmentTypes: {
            headerPopupIcon: `<b-button id="tooltip-adjustment-types-button" class="m-0 bg-transparent border-0 p-1">
                <svg xmlns="http://www.w3.org/2000/svg" height="1.3em" class="position-relative text-gray mb-1" viewBox="0 0 512 512">
                  <path fill="currentColor" d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 448c-110.532 0-200-89.431-200-200 0-110.495 89.472-200 200-200 110.491 0 200 89.471 200 200 0 110.53-89.431 200-200 200zm107.244-255.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C184.846 131.491 214.94 112 261.794 112c49.071 0 101.45 38.304 101.45 88.8zM298 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z">
                  </path>
                </svg>
              </b-button>`,
            headerPopup: `<span class="text-left title">${capitalize(this.$t("adjustment_reasons"))}:</span>
              <ul class="list-unstyled text-left m-0">
                <li>${this.$t("adjustment_scenario_0")}</li>
                <li>${this.$t("adjustment_scenario_1")}</li>
                <li>${this.$t("adjustment_scenario_3")}</li>
                <li>${this.$t("adjustment_scenario_4")}</li>
                <li>${this.$t("adjustment_scenario_7")}</li>
                <li>${this.$t("adjustment_scenario_13")}</li>
                <li>${this.$t("adjustment_scenario_14")}</li>
              </ul>`,
          },
          blockedTypes: {
            headerPopupIcon: `<b-button id="tooltip-adjustment-types-button" class="m-0 bg-transparent border-0 p-1">
                <svg xmlns="http://www.w3.org/2000/svg" height="1.3em" class="position-relative text-gray mb-1" viewBox="0 0 512 512">
                  <path fill="currentColor" d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 448c-110.532 0-200-89.431-200-200 0-110.495 89.472-200 200-200 110.491 0 200 89.471 200 200 0 110.53-89.431 200-200 200zm107.244-255.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C184.846 131.491 214.94 112 261.794 112c49.071 0 101.45 38.304 101.45 88.8zM298 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z">
                  </path>
                </svg>
              </b-button>`,
            headerPopup: `<span class="text-left title">${capitalize(this.$t("blocked_transactions"))}:</span>
              <ul class="list-unstyled text-left m-0">
                <li>${this.$t("adjustment_scenario_2")}</li>
                <li>${this.$t("adjustment_scenario_9")}</li>
                <li>${this.$t("adjustment_scenario_10")}</li>
              </ul>`,
          },
        },
        columnsClasses: {},
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        rowClasses: (row) => {
          const adjustmentTypes = row.adjustmentTypes
          const hasOnlyBlockedAdjustmentTypes = Array.isArray(adjustmentTypes) &&
            adjustmentTypes.length > 0 &&
            adjustmentTypes.every(item => Object.values(blockedAdjustmentTypes).includes(item))

          return hasOnlyBlockedAdjustmentTypes ? ["brighter-row"] : []
        },
        requestFunction (queryParams) {
          queryParams = { ...queryParams, ...this.$route.query }

          // Strip filters not available for the selected aggregation before sending to the backend
          const aggKey = queryParams.aggregate_by || "none"
          const allowedFilterParams = new Set(
            (aggregationFilters[aggKey] || aggregationFilters["none"]).map(k => snakeCase(k))
          )
          const alwaysAllowedParams = ["page", "per_page", "limit", "offset", "sorting", "aggregate_by", "apply_precision", "distribution_id"]
          const noneAggOnlyParams = aggKey === "none" ? ["show_adjustment", "show_blocked"] : []
          const systemParams = new Set([...alwaysAllowedParams, ...noneAggOnlyParams])
          Object.keys(queryParams).forEach(key => {
            const [baseKey] = key.split("__")
            if (!systemParams.has(key) && !allowedFilterParams.has(baseKey)) {
              delete queryParams[key]
            }
          })

          // Need to add the distribution period filters explicitly (only for aggregations that support tariff period)
          if (allowedFilterParams.has("tariff_period_year") && Array.isArray(queryParams.tariff_period_year)) {
            queryParams.tariff_period_year.forEach((year, index) => {
              const yearNumber  = Number(year)
              if (!isNaN(yearNumber) && index === 0) {
                queryParams.distribution_period_start__gte = `${yearNumber}-01-01`
              } else {
                queryParams.distribution_period_end__lt = `${yearNumber + 1}-01-01`
              }
            })
          }

          queryParams.aggregate_by = transactionAggregations[queryParams.aggregate_by] || transactionAggregations.none

          // replace transactions specific payout filters with actual filter queries
          if (queryParams.transaction_transaction_batch_id) {
            queryParams.transaction_batch_id = queryParams.transaction_transaction_batch_id
            delete queryParams.transaction_transaction_batch_id
          }
          if (queryParams.transaction_payout_completed_id) {
            queryParams.payout_completed_id = queryParams.transaction_payout_completed_id
            delete queryParams.transaction_payout_completed_id
          }

          return this.$api.transactions.transactionsList(queryParams)
        },
      }
      if (this.transactionsTable.selectedAggregation === "none") {
        // a lot of columns in this case, need to hardcode default width for it to be readable
        options.width = {
          "removeTransaction": "100",
          "refreshTransaction": "100",
          "transactionId": "120",
          "distributionId": "140",
          "distributionTariffCode": "120",
          "distributionPeriodStart": "100",
          "distributionPeriodEnd": "100",
          "recordingProprietaryId": "80",
          "rightsHolderProprietaryId": "120",
          "title": "100",
          "artist": "100",
          "isrc": "100",
          "localProprietaryIds": "100",
          "name": "140",
          "ipn": "100",
          "localRightsholderProprietaryIds": "120",
          "payeeId": "100",
          "paymentStatus": "100",
          "societyName": "120",
          "hold": "70",
          "preview": "60",
          "payable": "60",
          "mandateConflict": "60",
          "ownershipConflict": "60",
          "eligibility": "60",
          "lineupConflict": "60",
          "genreConflict": "60",
          "pendingPerformerMatching": "80",
          "pendingMakerMatching": "80",
          "totalAmount": "80",
          "payableAmount": "80",
          "paidNetAmount": "80",
          "paidDate": "120",
          "recordingBalance": "80",
          "payableRawAmount": "80",
          "totalPlaycount": "80",
          "type": "80",
          "performerCategory": "80",
          "share": "80",
          "payeeShare": "80",
          "payeeName": "80",
          "vpp": "80",
          "deltaTotalAmount": "80",
          "deltaPayableAmount": "80",
          "deltaPlaycount": "80",
          "calculationTimestamp": "120",
          "deltaShare": "80",
          "transactionsCount": "110",
          "payoutId": "80",
          "baseFee": "80",
          "internationalIncomingFee": "80",
          "serviceFee": "80",
          "withholdingTax": "80",
          "adjustmentTypes": "120",
          "blockedTypes": "120",
          "distributionLicenseeName": "120",
          "distributionInvoiceNumber": "80",
        }
      }
      return options
    },
    transactionsOptions () {
      return [
        {
          value: "transactions_tab",
          text: capitalize(this.$t("transactions")),
        },
        {
          value: "payouts_tab",
          text: capitalize(this.$t("payouts")),
          disabled: this.disablePayoutsTab,
        },
      ]
    },
    inapplicableFilterLabels () {
      const targetAgg = this.pendingAggregation ? this.pendingAggregation.next : this.transactionsTable.selectedAggregation
      return this.getInapplicableFilterLabels(this.filters, targetAgg)
    },
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    ...mapActions("consts", ["getTariffs"]),
    parseDate,
    getProprietaryIds (identifiers) {
      let result = []
      identifiers.filter(id => (id.type !== "isrc" && id.type !== "ipn" && id.type !== "salesforce_id")
      ).forEach(
        identifiers => {
          identifiers.codes.forEach(code => {
            result.push({
              type: identifiers.type,
              code: code,
            })
          })
        }
      )
      return result
    },
    showModal (identifier) {
      this.$refs[identifier].show()
    },
    onTabChange ({ index }) {
      this.currentTab = this.tabs[index].ref
    },
    getData () {
      this.$refs.transactions?.getData()
    },
    onLoaded ({ data }) {
      this.count = data?.count || 0
      this.loading = false
      this.fetchPendingRemovalRowKeys()
    },
    exportData () {
      this.$bvModal.show("transactions-export-modal")
    },
    onFilter (name, value) {
      if (name === "distribution") {
        if (value === null) {
          this.changeFilter(name, value)
          this.changeFilter("distribution_id", value)
        } else {
          this.changeFilter(name, value.value)
          this.changeFilter("distribution_id", value.id)
        }
      } else if (name === "paymentStatus") {
        this.changeFilter(name, value)
      } else if (name === "search") {
        if (value === "") {
          this.changeFilter(name, null)
        } else {
          this.changeFilter(name, value)
        }
      } else if (name === "rightsHolderProprietaryId") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.resoundId)
        }
      } else if (name === "recordingProprietaryId") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.resoundId)
        }
      } else if (["calculationTimestamp"].includes(name)) {
        const timestampRange = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT),
          ] : []
        if (timestampRange.filter(r => r).length) {
          this.changeFilter("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
          this.changeFilter(name, timestampRange)
        } else {
          this.changeFilter("userTimezone", null)
          this.changeFilter(name, null)
        }
      } else if (name === "distributionPeriodStart" || name === "distributionPeriodEnd") {
        const periodRange = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT),
          ] : []
        if (periodRange.filter(r => r).length) {
          this.changeFilter("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
          this.changeFilter(name, periodRange)
        } else {
          this.changeFilter("userTimezone", null)
          this.changeFilter(name, null)
        }
      } else if (name === "paidDate") {
        const paidAmountRange = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT),
          ] : []
        if (paidAmountRange.filter(r => r).length) {
          this.changeFilter("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
          this.changeFilter(name, paidAmountRange)
        } else {
          this.changeFilter("userTimezone", null)
          this.changeFilter(name, null)
        }
      } else if (name === "transactionPayoutCompletedId" || name === "transactionTransactionBatchId") {
        this.changeFilter(name, value)
        if (!this.isSubTab) {
          if (value) {
            this.filters.payable.value = "1"
            this.filters.abovePayoutThreshold.value = "1"
            this.changeFilter("payable", "1")
            this.changeFilter("abovePayoutThreshold", "1")
          } else {
            this.$refs.transactionFilters?.removeFilter("payable", false)
            this.$refs.transactionFilters?.removeFilter("abovePayoutThreshold", false)
          }
        }
      }
    },
    onSorted (data) {
      this.queryParams = {
        ...this.queryParams,
        ["sorting"]: data.map(item => item.ascending ? toSortParam(item.column) : `-${toSortParam(item.column)}`)
      }
      this.applyFilters()
    },
    onFiltersApply () {
      if (!this.firstLoaded && this.filtersPending) {
        this.firstLoaded = true
      }
      this.applyFilters()
      // This is to fix the problem on general transaction view where we don't have payoutData set
      const batchId = this.filters.transactionTransactionBatchId.value
      if ( batchId && !this.actualPayoutData?.transactionBatchId !== batchId) {
        this.$api.payouts.payoutsList({ transaction_batch_id: batchId }).then(response => {
          this.filteredPayoutData = response?.data?.results?.[0]
        })
      } else {
        this.filteredPayoutData = null
      }
    },
    loadAllTransactions () {
      this.firstLoaded = true
      this.$nextTick(() => {
        this.getData()
      })
    },
    handleRemoveTransaction (row) {
      this.currentTransaction = row
      this.$bvModal.show("removal-modal")
    },
    handleRefreshTransaction (row) {
      this.currentTransaction = row
      this.$bvModal.show("refresh-modal")
    },
    cancelRefresh () {
      this.currentTransaction = null
      this.$bvModal.hide("refresh-modal")
    },
    cancelRemove () {
      this.currentTransaction = null
      this.$bvModal.hide("removal-modal")
      this.removeTransaction.comment = ""
      this.removeTransaction.reason = null
    },
    transactionRefreshRequest () {
      let data = {
        aggregationLevel: this.getAggregationForRemoval(this.transactionsTable.selectedAggregation),
        payoutId: this.actualPayoutData.id,
        distributionId: this.currentTransaction.distributionId,
        societyName: this.currentTransaction.societyName,
        distributionDescription: this.currentTransaction.distributionDescription,
        payeeId: this.currentTransaction.payeeId,
        payeeName: this.currentTransaction.payeeName,
        playcount: this.currentTransaction.deltaPlaycount,
        amount: this.currentTransaction.deltaTotalAmount,
        share: this.currentTransaction.share,
        tariff: { code: this.currentTransaction?.distributionTariffCode },
        transactionsCount: this.currentTransaction.transactionsCount,
        distributionStartDate: this.currentTransaction?.distributionPeriodStart,
        distributionEndDate: this.currentTransaction?.distributionPeriodStart,
      }

      if (this.currentTransaction.recordingProprietaryId ){
        data.recording = { resoundId: this.currentTransaction.recordingProprietaryId }
      }

      if (this.currentTransaction.rightsHolderProprietaryId ) {
        data.rightsHolder = { resoundId: this.currentTransaction.rightsHolderProprietaryId }
      }
      this.$api.payouts.createTransactionRefreshRequest(data).then(() => {
        this.success(capitalize(this.$t("refresh_request_success")))
        this.currentTransaction = null
        this.$bvModal.hide("refresh-modal")
        this.getData()
      }).catch(error => {
        const { status } = error.response
        if (status === 400) {
          this.error(capitalize(this.$t("refresh_request_fail")))
        }
      })
    },
    transactionRemovalRequest () {
      let data = {
        aggregationLevel: this.getAggregationForRemoval(this.transactionsTable.selectedAggregation),
        payoutId: this.actualPayoutData.id,
        comment: this.removeTransaction.comment,
        reason: this.removeTransaction.reason,
        payeeId: this.currentTransaction.payeeId,
        payeeName: this.currentTransaction.payeeName,
        distributionId: this.currentTransaction.distributionId,
        distributionDescription: this.currentTransaction.distributionDescription,
        societyName: this.currentTransaction.societyName,
        playcount: this.currentTransaction.deltaPlaycount,
        amount: this.currentTransaction.deltaTotalAmount,
        share: this.currentTransaction.share,
        transactionsCount: this.currentTransaction.transactionsCount,
        distributionStartDate: this.currentTransaction?.distributionPeriodStart,
        distributionEndDate: this.currentTransaction?.distributionPeriodStart,
      }

      if (this.currentTransaction.recordingProprietaryId ){
        data.recording = { resoundId: this.currentTransaction.recordingProprietaryId }
      }

      if (this.currentTransaction.rightsHolderProprietaryId ) {
        data.rightsHolder = { resoundId: this.currentTransaction.rightsHolderProprietaryId }
      }

      this.$api.payouts.createTransactionRemovalRequest(data).then(() => {
        this.success(this.$t("removal_request_success"))
        this.$bvModal.hide("removal-modal")
        this.getData()
      }).catch(error => {
        const { status } = error.response
        if (status === 400) {
          this.error(capitalize(this.$t("removal_request_fail")))
        }
      })
      this.removeTransaction.comment = ""
      this.removeTransaction.reason = null
    },
    getAggregationForRemoval (value) {
      if (value === "rightsHolder") {
        return "rights_holder"
      } else if (value === "rightsHolderTariffRecording") {
        return "recording_rights_holder_distribution"
      } else if (value === "recordingRightsHolder"){
        return "recording_rights_holder"
      }
    },
    removalMessage () {
      return `You are about to submit a request to remove ${this.currentTransaction?.rights_holder_proprietary_id} for ${this.currentTransaction?.name}, with ${this.currentTransaction?.transactionsCount} transactions and ${this.currentTransaction?.deltaTotalAmount} gross amount. Please select a reason for your request from the list to proceed.`
    },
    refreshMessage () {
      return `You are about to submit a transactions refresh request for ${this.currentTransaction?.name}`
    },
    applyShowBlockedFilter () {
      if (this.filters.showBlocked.value) {
        this.$refs.transactionFilters.addFilter("showBlocked", this.filters.showBlocked.value)
      } else {
        this.$refs.transactionFilters.removeFilter("showBlocked")
      }
    },
    applyPrecisionFilter () {
      if (this.filters.applyPrecision.value) {
        this.$refs.transactionFilters.addFilter("applyPrecision", this.filters.applyPrecision.value)
      } else {
        this.$refs.transactionFilters.removeFilter("applyPrecision")
      }
    },
    applyShowAdjustmentFilter () {
      if (this.filters.showAdjustment.value) {
        this.$refs.transactionFilters.addFilter("showAdjustment", this.filters.showAdjustment.value)
      } else {
        this.$refs.transactionFilters.removeFilter("showAdjustment")
      }
    },
    applyPayoutFilter (filterName, id) {
      this.$nextTick(() => {
        this.$refs.transactionFilters.$nextTick(() => {
          this.$refs.transactionFilters.addFilter(filterName, id)
          if (filterName === "transactionPayoutCompletedId") {
            this.$refs.transactionFilters.removeFilter("transactionTransactionBatchId", false)
          } else if (filterName === "transactionTransactionBatchId") {
            this.$refs.transactionFilters.removeFilter("transactionPayoutCompletedId", false)
          }
          this.$refs.transactionFilters.addFilter("payable", "1")
          this.$refs.transactionFilters.addFilter("abovePayoutThreshold", "1")
        })
      })

      if (!this.isSubTab) {
        this.$refs.transactionsTabs.tabIndex = this.tabs.findIndex(tab => tab.ref === "transactions_tab")
        this.firstLoaded = true
      }
    },
    handleCloseRemovalRequest () {
      this.isRemovalRequest = false
    },
    getPaymentStatusDisplay (paymentStatus) {
      if (!paymentStatus) {
        return ""
      }
      let statusCode = transactionPaymentStatus[paymentStatus.toUpperCase()]
      return this.$t(statusCode)
    },
    tariffDisplay (code) {
      let tariff = this.tariffs.find(t => t.code === code)
      if (tariff) {
        return `${tariff.code} - ${tariff.text}`
      }
      return ""
    },
    userMemberCollectiveMatchesRow (row) {
      let rowMemberCollective = row.societyName || row.payeeName
      let userMemberCollective = this.user.selectedMember ? this.user.selectedMember.name : this.user.memberCollective.name
      return rowMemberCollective === userMemberCollective
    },
    confirmAggregationChange () {
      const { next } = this.pendingAggregation
      const allowed = this.getAllowedFilters(next)
      Object.entries(this.filters).forEach(([key, filter]) => {
        const isActive = this.isFilterActive(filter.value, filter.defaultValue)
        if (isActive && !allowed.has(key)) {
          this.$refs.transactionFilters?.removeFilter(key)
          filter.value = filter.defaultValue !== undefined ? filter.defaultValue : null
        }
      })
      this.committedAggregation = next
      this.pendingAggregation = null
      this.transactionsTable.selectedAggregation = next
    },
    cancelAggregationChange () {
      this.pendingAggregation = null
    },
    isFilterActive (filterValue, defaultValue) {
      if (Array.isArray(filterValue)) {
        return filterValue.some(value => value !== null && value !== "")
      }
      if (typeof defaultValue === "boolean") {
        return filterValue !== defaultValue
      }
      return filterValue !== null && filterValue !== "" && filterValue !== defaultValue
    },
    getAllowedFilters (aggregation) {
      const allowed = new Set(aggregationFilters[aggregation] || aggregationFilters["none"])
      if (this.isSubTab || ["transactionPayoutCompletedId", "transactionTransactionBatchId"].some(filterName => this.filters[filterName]?.value)) {
        allowed.add("payable")
        allowed.add("abovePayoutThreshold")
      }
      return allowed
    },
    hasInapplicableFilters (filters, targetAggregation) {
      const allowed = this.getAllowedFilters(targetAggregation)
      return Object.entries(filters).some(([key, filter]) => {
        const isActive = this.isFilterActive(filter.value, filter.defaultValue)
        return isActive && !allowed.has(key)
      })
    },
    getInapplicableFilterLabels (filters, targetAggregation) {
      const allowed = this.getAllowedFilters(targetAggregation)
      return Object.entries(filters)
        .filter(([key, filter]) => {
          const isActive = this.isFilterActive(filter.value, filter.defaultValue)
          return isActive && !allowed.has(key)
        })
        .map(([, filter]) => filter.label)
    },
    removalPairingKey (parts) {
      const n = (v) => (v === null || v === undefined ? "" : String(v).trim())
      return [
        n(parts.aggregationLevel),
        n(parts.payoutId),
        n(parts.payeeId),
        n(parts.distributionId),
        n(parts.distributionDescription),
        n(parts.societyName),
        n(parts.rightsHolderResoundId),
        n(parts.recordingResoundId),
      ].join("\t")
    },
    removeRowDisabled (row) {
      const agg = this.getAggregationForRemoval(this.transactionsTable.selectedAggregation)
      const payoutId = this.actualPayoutData?.id
      if (!agg || !payoutId) {
        return false
      }
      const k = this.removalPairingKey({
        aggregationLevel: agg,
        payoutId,
        payeeId: row.payeeId,
        distributionId: row.distributionId,
        distributionDescription: row.distributionDescription,
        societyName: row.societyName,
        rightsHolderResoundId: row.rightsHolderProprietaryId,
        recordingResoundId: row.recordingProprietaryId,
      })
      return !!this.pendingRemovalRowKeys[k]
    },
    async fetchPendingRemovalRowKeys () {
      const payoutId = this.actualPayoutData?.id
      if (!payoutId || !this.filters.transactionTransactionBatchId?.value) {
        this.pendingRemovalRowKeys = {}
        return
      }
      const sel = this.transactionsTable.selectedAggregation
      const aggregationLevel = pendingQueueAggregations[sel] || this.getAggregationForRemoval(sel)
      if (!aggregationLevel) {
        this.pendingRemovalRowKeys = {}
        return
      }
      try {
        const { data } = await this.$api.payouts.getTransactionsRemovalList({
          payout_id: payoutId,
          aggregation_level: aggregationLevel,
          limit: 2000,
          page: 1,
        })
        const keys = {}
        for (const req of data.results || []) {
          if (req.status === transactionRemovalStatuses.REJECTED) {
            continue
          }
          if (req.status === transactionRemovalStatuses.APPROVED && req.executionStatus === payoutJobExecutionStatuses.ERROR) {
            continue
          }
          const k = this.removalPairingKey({
            aggregationLevel: req.aggregationLevel,
            payoutId: req.payoutId,
            payeeId: req.payeeId,
            distributionId: req.distributionId,
            distributionDescription: req.distributionDescription,
            societyName: req.societyName,
            rightsHolderResoundId: req.rightsHolder?.resoundId,
            recordingResoundId: req.recording?.resoundId,
          })
          if (k) {
            keys[k] = true
          }
        }
        this.pendingRemovalRowKeys = keys
      } catch {
        this.pendingRemovalRowKeys = {}
      }
    },
  },
  watch: {
    typeFilterOptions: {
      immediate: true,
      handler (options) {
        this.filters.type.options = options
      },
    },
    "transactionsTable.selectedAggregation": function (value, oldValue) {
      if (this.pendingAggregation) {
        return
      }
      const hasInapplicable = this.hasInapplicableFilters(this.filters, value)
      if (hasInapplicable) {
        this.pendingAggregation = { next: value, prev: oldValue }
        this.$nextTick(() => {
          this.transactionsTable.selectedAggregation = oldValue
          this.$bvModal.show("aggregation-filters-warning-modal")
        })
        return
      }
      this.committedAggregation = value
      delete this.queryParams["sorting"]
      this.queryParams = {
        ...this.queryParams, "aggregate_by": value
      }
      this.changeFilter("aggregateBy", value, true)
    },
    actualPayoutData: function () {
      this.fetchPendingRemovalRowKeys()
    },
    // In case if page is mounted before memberCollectives and tariffs loaded to store
    "$store.state.consts.memberCollectives": function (value) {
      this.filters.societyName.options = value.map(x => ({
        text: x.name,
        value: x.name,
      }))
    },
    "$store.state.consts.tariffs": function (value) {
      this.filters.distributionTariffCode.options = value.map(t => ({
        text: `${t.code} - ${t.text}`, value: t.code
      }))
    }
  },
  data () {
    return {
      filteredPayoutData: null,
      firstLoaded: false,
      count: 0,
      page: 1,
      pageNumber: 1,
      limit: perPageItemsMd,
      loading: true,
      queryParams: {},
      pendingRemovalRowKeys: {},
      useGtLtFilters: true,
      blockedAdjustmentTypes,
      linkTypes,
      Pages,
      transactionPerformerCategories,
      disablePayoutsTab: false,
      transactionsTable: {
        aggregationOptions: [
          {
            value: "none",
            text: capitalize(this.$t("none")),
          },
          {
            value: "payeeTariff",
            text: this.$t("payee_tariff"),
          },
          {
            value: "rightsHolder",
            text: this.$t("rights_holder_title"),
          },
          {
            value: "rightsHolderTariffPeriod",
            text: this.$t("rights_holder_tariff_period"),
          },
          {
            value: "recordingRightsHolder",
            text: this.$t("rights_holder_recording"),
          },
          {
            value: "rightsHolderTariffRecording",
            text: this.$t("rights_holder_tariff_recording"),
          },
        ],
        selectedAggregation: "none",
      },
      removalOptions: [
        {
          value: "unidentified_mandate_conflict",
          text: this.$t("unidentified_mandate_conflict"),
        },
        {
          value: "loss_of_mandate",
          text: this.$t("loss_of_mandate"),
        },
        {
          value: "unidentified_conflict",
          text: this.$t("unidentified_conflict"),
        },
        {
          value: "general_rejection",
          text: this.$t("general_rejection"),
        },
      ],
      removeTransaction: {
        reason: null,
        comment: ""
      },
      filters: {
        search: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("search")), noWatch: true },
        userTimezone: { value: null, defaultValue: null, hidden: true },
        payeeId: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("payee_id"),
          hidden: true,
        },
        paymentStatus: {
          value: null,
          defaultValue: null,
          options: Object.entries(transactionPaymentStatus).map(([key, value]) => ({
            value: value,
            text: this.$t(key.toLowerCase())
          })),
          emptyLabel: capitalize(this.$t("all")),
          emptyValue: null,
          type: filterTypes.SELECT,
          dataType: String,
          label: this.$t("payment_status"),
          hidden: true,
        },
        payeeName: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("payee_name"),
          hidden: true,
        },
        distributionInvoiceNumber: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("invoice_number")),
          hidden: true,
        },
        distributionLicenseeName: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("licensee_name")),
          hidden: true,
        },
        distributionTariffCode: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("tariff")),
          options: [],
          valueField: "distributionTariffCode",
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
        },
        tariffPeriodYear: {
          value: null,
          defaultValue: null,
          placeholder: `${capitalize(this.$t("tariff_period_title"))} ${this.$t("year")}`,
          type: filterTypes.YEAR_RANGE,
          label: `${capitalize(this.$t("tariff_period_title"))} ${this.$t("year")}`,
        },
        distribution: {
          value: null,
          defaultValue: null,
          excludeName: "description",
          placeholder: capitalize(this.$t("search_tariff_period")),
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("tariff_period_title")),
          valueField: "description",
          textField: "description",
          searchName: "description",
          listEndpoint: this.$api.distributions.distributionsList,
          noWatch: true,
        },
        distributionPeriodStart: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("tariff_period_start")),
          noWatch: true,
          hidden: true,
        },
        distributionPeriodEnd: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("tariff_period_end")),
          noWatch: true,
          hidden: true,
        },
        rightsHolderProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
        ipn: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("ipn"),
        },
        rightsholderIdentifierCode: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("rightsholder_proprietary_id")),
        },
        recordingProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("recording")),
          placeholder: capitalize(this.$t("search_recording")),
          excludeName: "id",
          valueField: "resoundId",
          textFunction: (r => `${r.artist} - ${r.title}`),
          searchName: "fuzzy_search",
          listEndpoint: this.$api.repertoire.recordingsList,
          noWatch: true,
        },
        societyName: {
          value: null,
          defaultValue: null,
          options: [],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("collective_society"),
          hidden: true,
        },
        type: {
          value: null,
          defaultValue: null,
          options: [],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("type")),
          hidden: true,
        },
        performerCategory: {
          value: null,
          defaultValue: null,
          options: Object.entries(transactionPerformerCategories).map(([k, v]) => ({ value: k, text: capitalize(this.$t(v)) })),
          emptyValue: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: `${capitalize(this.$t("performer"))} ${this.$t("category")}`,
          hidden: true,
        },
        artist: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("artist")),
          hidden: true,
        },
        isrc: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("isrc"),
          hidden: true,
        },
        totalPlaycount: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("playcount")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        vpp: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("vpp")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        totalAmount: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("amount_gross")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        internationalIncomingFee: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("international_incoming_fees")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        baseFee: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("base_fees")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        serviceFee: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("service_fees")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        withholdingTax: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("withholding_tax")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          singleBound: false,
          hidden: true,
        },
        calculationTimestamp: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("transaction_calculation_date")),
          noWatch: true,
          hidden: true,
        },
        paidDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("paid_date")),
          noWatch: true,
          hidden: true,
        },
        eligibility: {
          value: null,
          defaultValue: null,
          options: [
            { value: "ineligible", text: "Ineligible" },
            { value: "eligible", text: "Eligible" },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("eligible")),
          hidden: true,
        },
        payable: {
          value: this.payoutData?.id ? "1" : null,
          defaultValue: null,
          disable: false,
          options: [
            { value: "0", text: capitalize(this.$t("no")) },
            { value: "1", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          emptyValue: null,
          type: filterTypes.SELECT,
          dataType: String,
          label: capitalize(this.$t("payable")),
        },
        abovePayoutThreshold: {
          value: this.payoutData?.id ? "1" : null,
          defaultValue: null,
          disable: false,
          options: [
            { value: "0", text: capitalize(this.$t("no")) },
            { value: "1", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          emptyValue: null,
          type: filterTypes.SELECT,
          dataType: String,
          label: capitalize(this.$t("above_threshold")),
        },
        lineupConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_lineup_conflict_title")),
        },
        mandateConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_mandate_conflict")),
        },
        hold: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("no_hold"),
        },
        genreConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("no_genre_conflict"),
        },
        missingMandate: {
          value: null,
          defaultValue: null,
          options: [
            { value: "true", text: capitalize(this.$t("no")) },
            { value: "false", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("mandate")),
        },
        hasFeaturedCount: {
          value: null,
          defaultValue: null,
          options: [
            { value: "false", text: capitalize(this.$t("no")) },
            { value: "true", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("featured_count")),
        },
        moratorium: {
          value: null,
          defaultValue: null,
          options: [
            { value: "0", text: capitalize(this.$t("yes")) },
            { value: "1", text: capitalize(this.$t("no")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_moratorium")),
        },
        validPayee: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("yes")) },
            { value: "0", text: capitalize(this.$t("no")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("valid_payee")),
        },
        recordingBalanceExceeded: {
          value: null,
          defaultValue: null,
          options: [
            { value: "0", text: capitalize(this.$t("yes")) },
            { value: "1", text: capitalize(this.$t("no")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_recording_balance_exceeded")),
        },
        hasNonFeaturedCount: {
          value: null,
          defaultValue: null,
          options: [
            { value: "false", text: capitalize(this.$t("no")) },
            { value: "true", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("non_featured_count"),
        },
        pendingMakerMatching: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("matched_maker"),
        },
        pendingPerformerMatching: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: this.$t("matched_performer"),
        },
        payableAmount: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("net_amount")),
        },
        recordingBalance: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("recording_balance")),
        },
        paidNetAmount: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("paid_net_amount")),
        },
        payableRawAmount: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("payable_raw_amount")),
        },
        share: {
          value: null,
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("share")),
        },
        ownershipConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_ownership_conflict")),
        },
        transactionPayoutCompletedId: {
          value: !this.payoutData?.transactionBatchId && this.payoutData?.payoutId ? this.payoutData?.payoutId : null,
          defaultValue: !this.payoutData?.transactionBatchId && this.payoutData?.payoutId ? this.payoutData?.payoutId : null,
          disabled: !this.payoutData?.transactionBatchId && !!this.payoutData?.payoutId,
          type: filterTypes.TEXT,
          label: this.$t("payout_ID"),
        },
        transactionTransactionBatchId: {
          value: this.payoutData?.transactionBatchId || null,
          defaultValue: this.payoutData?.transactionBatchId || null,
          disabled: !!this.payoutData?.transactionBatchId,
          type: filterTypes.TEXT,
          label: this.$t("batch_id"),
        },
        closeoutId: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: this.$t("closeout_id"),
        },
        showBlocked: {
          value: false,
          defaultValue: false,
          type: filterTypes.BOOLEAN,
          label: this.$t("show_blocked"),
        },
        applyPrecision: {
          value: true,
          defaultValue: true,
          type: filterTypes.BOOLEAN,
          label: this.$t("apply_rounding"),
        },
        identifiersCodes: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("proprietary_id")),
        },
        showAdjustment: {
          value: false,
          defaultValue: false,
          type: filterTypes.BOOLEAN,
          label: this.$t("show_adjustment"),
        },
      },
      preview: {
        value: null,
        defaultValue: null,
        options: [
          { value: "0", text: capitalize(this.$t("no")) },
          { value: "1", text: capitalize(this.$t("yes")) },
        ],
        emptyLabel: capitalize(this.$t("all")),
        type: filterTypes.SELECT,
        label: capitalize(this.$t("preview_flag")),
      },
      userPreferenceNames,
      currentTab: "transactions_tab",
      tabs: [
        {
          ref: "transactions_tab",
          title: capitalize(this.$t("transactions")),
        },
        {
          ref: "payouts_tab",
          title: capitalize(this.$t("payouts")),
        },
      ],
      localProprietaryIdsOptions: {
        sortable: [],
        headings: {
          id: capitalize(this.$t("id")),
          mc: capitalize(this.$t("mc")),
        }
      },
      localProprietaryIdsColumns: [
        "id",
        "mc",
      ],
      drillDownTableOptions: {
        sortable: [],
        headings: {
          totalAmount: capitalize(this.$t("amount_gross")),
          rawAmount: capitalize(this.$t("amount_raw")),
          payableAmount: capitalize(this.$t("net_amount")),
          totalPlaycount: capitalize(this.$t("total_playcount")),
          share: capitalize(this.$t("share")),
          name: capitalize(this.$t("rights_holder_name")),
          payable: capitalize(this.$t("payable")),
          baseFee: capitalize(this.$t("base_fees")),
          internationalIncomingFee: capitalize(this.$t("international_incoming_fees")),
          serviceFee: capitalize(this.$t("service_fees")),
          withholdingTax: capitalize(this.$t("withholding_tax")),
          broadcastStartDate: capitalize(this.$t("broadcast_start")),
          broadcastEndDate: capitalize(this.$t("broadcast_end")),
        }
      },
      drillDownTableColumns: [
        "payable",
        "name",
        "totalAmount",
        "rawAmount",
        "payableAmount",
        "totalPlaycount",
        "share",
        "broadcastStartDate",
        "broadcastEndDate",
        "baseFee",
        "internationalIncomingFee",
        "serviceFee",
        "withholdingTax",
      ],
      currentTransaction: null,
      isRemovalRequest: false,
      pendingAggregation: null,
      committedAggregation: "none",
    }
  },
  mounted () {
    this.changeFilter("apply_precision", this.filters.applyPrecision.defaultValue)
    this.getTariffs()
    this.transactionsTable.selectedAggregation = this.$route.query["aggregate_by"] || "none"
    this.committedAggregation = this.transactionsTable.selectedAggregation
    const filtersFromQuery = Object.keys(this.$route.query).filter(
      key => !["sorting", "aggregate_by", "page", "limit"].includes(key)
    )

    if (filtersFromQuery.length > 0) {
      this.firstLoaded = true
      filtersFromQuery.forEach(filter => {
        if (
          filter === "recordingProprietaryId" ||
          filter === "rightsHolderProprietaryId" ||
          filter === "distribution_id" ||
          filter === "distribution"
        ) {
          this.changeFilter(filter, this.$route.query[filter])
          if (filter === "distribution_id") {
            this.filters["distribution"].searchValue = this.$route.query[filter]
          } else {
            this.filters[filter].searchValue = this.$route.query[filter]
          }
        }
      })
    }
    // Set options when loaded
    this.filters.societyName.options = this.memberCollectives.map(x => ({
      text: x.name,
      value: x.name,
    }))

    if (this.isSubTab) {
      if (this.recordingResoundId) {
        this.changeFilter("recordingProprietaryId", this.recordingResoundId)
      } else if (this.rightsholderResoundId) {
        this.changeFilter("rightsHolderProprietaryId", this.rightsholderResoundId)
      } else if (this.distributionId) {
        this.changeFilter("distribution_id", this.distributionId)
        this.firstLoaded = true
      } else if (this.payoutData?.transactionBatchId) {
        this.changeFilter("transactionTransactionBatchId", this.payoutData.transactionBatchId)
        if (this.filters.abovePayoutThreshold.value) {
          this.changeFilter("abovePayoutThreshold", this.filters.abovePayoutThreshold.value)
        }
        if (this.filters.payable.value) {
          this.changeFilter("payable", this.filters.payable.value)
        }
        if (!this.firstLoaded) {
          this.$nextTick(() => {
            this.$refs.transactionFilters.addFilter("transactionTransactionBatchId", this.payoutData.transactionBatchId)
            this.$refs.transactionFilters.removeFilter("transactionPayoutCompletedId", false)
            this.$refs.transactionFilters.addFilter("abovePayoutThreshold", "1")
            this.$refs.transactionFilters.addFilter("payable", "1")
          })
          this.firstLoaded = true
        }
      } else if (this.payoutData?.payoutId){
        this.changeFilter("transactionPayoutCompletedId", this.payoutData.payoutId)
        if (this.filters.abovePayoutThreshold.value) {
          this.changeFilter("abovePayoutThreshold", this.filters.abovePayoutThreshold.value)
        }
        if (this.filters.payable.value) {
          this.changeFilter("payable", this.filters.payable.value)
        }
        if (!this.firstLoaded) {
          this.$nextTick(() => {
            this.$refs.transactionFilters.addFilter("transactionPayoutCompletedId", this.payoutData.payoutId)
            this.$refs.transactionFilters.removeFilter("transactionTransactionBatchId", false)
            this.$refs.transactionFilters.addFilter("abovePayoutThreshold", "1")
            this.$refs.transactionFilters.addFilter("payable", "1")
          })
          this.firstLoaded = true
        }
      }
    }
  },
}

</script>

<style lang="scss" scoped>

.no-filters-text {
  font-size: 1.2rem;
  width: 350px;
  margin-bottom: 2rem;
}

::v-deep .no-title {
  padding-top: 15px;
}

.no-filter-img {
  width: calc(100dvh - 380px);
  max-width: 750px;
  min-width: 360px;
}
</style>
